import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import { BaseEntity } from './base.entity';
import { ClientRegistrationNewFileEntity } from './client-registration-new-file.entity';
import { ClientEntity } from './client.entity';
import { PortalSiteEntity } from './portal-site.entity';

@Index('client_registration_new_fk_client__id_fk', ['fkClientId'], {})
@Index('client_registration_new_fk_portal_site__id', ['fkPortalSiteId'], {})
@Entity('client_registration_new')
export class ClientRegistrationNewEntity extends BaseEntity {
	@Column('varchar', { name: 'name', nullable: true, length: 255 })
	public name!: string | null;

	@Column('varchar', { name: 'ownerName', nullable: true, length: 255 })
	public ownerName!: string | null;

	@Column('varchar', { name: 'buyerName', nullable: true, length: 255 })
	public buyerName!: string | null;

	@Column('varchar', { name: 'address1', nullable: true, length: 255 })
	public address1!: string | null;

	@Column('varchar', { name: 'address2', nullable: true, length: 255 })
	public address2!: string | null;

	@Column('varchar', { name: 'city', nullable: true, length: 255 })
	public city!: string | null;

	@Column('varchar', { name: 'state', nullable: true, length: 255 })
	public state!: string | null;

	@Column('varchar', { name: 'zip', nullable: true, length: 255 })
	public zip!: string | null;

	@Column('varchar', { name: 'email', nullable: true, length: 255 })
	public email!: string | null;

	@Column('varchar', { name: 'phone', length: 255 })
	public phone!: string;

	@Column('varchar', { name: 'secondaryPhone', nullable: true, length: 255 })
	public secondaryPhone!: string | null;

	@Column('varchar', { name: 'fax', nullable: true, length: 255 })
	public fax!: string | null;

	@Column('int', { name: 'fk_client__id', nullable: true, unsigned: true })
	public fkClientId!: number | null;

	@Column('varchar', { name: 'status', length: 255, default: () => "'1'" })
	public status!: string;

	// --- SM2 config-driven registration (PD-14329). This legacy SM1 table is
	// reused as the customer-registration submission store. Everything above is
	// the fixed/standard field set; the three columns below carry the new
	// config-driven payload. Additive only — legacy rows simply have these
	// NULL. ---

	// Which storefront (portal_site) this submission came from. Null for legacy
	// SM1 rows / non-portal submissions. Also identifies which registration_field
	// scope the fieldSnapshot was taken from.
	@Column('int', { name: 'fk_portal_site__id', nullable: true, unsigned: true })
	public fkPortalSiteId!: number | null;

	// Admin-configured supplemental answers, keyed by registration_field.fieldKey.
	// Holds ONLY the config-driven questions (standard fields live in their own
	// columns above). Surfaced on the client-detail Recent Submissions card + the
	// summary email; intentionally NOT persisted onto the client entity.
	@Column('json', { name: 'registrationFields', nullable: true })
	public registrationFields!: Record<string, unknown> | null;

	// Snapshot of the registration_field definitions (label / type / options) at
	// submit time, so a historical submission stays readable after the live
	// config is edited.
	@Column('json', { name: 'fieldSnapshot', nullable: true })
	public fieldSnapshot!: Array<Record<string, unknown>> | null;

	@OneToMany(() => ClientEntity, (client) => client.fkClientRegistrationNew, {})
	public clients!: Array<ClientEntity>;

	@ManyToOne(() => ClientEntity, (client) => client.clientRegistrationNews, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_client__id', referencedColumnName: 'id' }])
	public fkClient!: ClientEntity;

	@OneToMany(
		() => ClientRegistrationNewFileEntity,
		(clientRegistrationNewFile) =>
			clientRegistrationNewFile.fkClientRegistrationNew,
		{},
	)
	public clientRegistrationNewFiles!: Array<ClientRegistrationNewFileEntity>;

	// Logical relation only (no DB-level FK constraint) so additive schema-sync
	// just creates the column + index. A deleted portal_site must not delete the
	// historical submission, so this stays a soft pointer.
	@ManyToOne(() => PortalSiteEntity, {
		nullable: true,
		createForeignKeyConstraints: false,
	})
	@JoinColumn([{ name: 'fk_portal_site__id', referencedColumnName: 'id' }])
	public fkPortalSite!: PortalSiteEntity | null;
}
